import React from 'react';
import { Dropdown } from 'freemium-ui';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  ConditionType,
  ContactFormType,
  FilterOperationType,
  FormServFieldType,
  UserType,
} from '../../../../../shared/types';
import Icon from '../../../../../shared/components/Icon';
import {
  ALL_FIELDS,
  ALL_FIELDS_BY_NAME,
  BACK_END_FIELDS,
  FieldBasedFilterOperations,
  FilterOperations,
  TEMPORARY_HIDDEN_FIELDS,
} from '../../../../../shared/constants';
import FilterOperation from '../../../../components/Filters/FilterOperation';
import { ConditionRowContainer } from './styles';

type SegmentConditionRowProps = {
  onDelete: (id: string) => void;
  onChange: (v: any) => void;
  disabled?: boolean;
  showError: boolean;
  key?: string;
  errors: any;
  contactForm: ContactFormType;
  condition: ConditionType;
  userInfo?: UserType;
};

const SegmentConditionRow = (props: SegmentConditionRowProps) => {
  const { t, i18n } = useTranslation();
  const {
    errors = {},
    contactForm,
    key,
    disabled = false,
    showError = false,
    condition,
    onChange,
    onDelete,
    userInfo,
  } = props;
  const { fields } = contactForm;

  const fieldsAvailableForSegments = fields
    ?.filter(
      (availableField: FormServFieldType) =>
        Object.keys(FieldBasedFilterOperations).includes(
          `${availableField.type}`,
        ) &&
        ![...TEMPORARY_HIDDEN_FIELDS, ...BACK_END_FIELDS].includes(
          availableField.name,
        ),
    )
    .map((f) => {
      return {
        ...f,
        label: i18n.exists(
          `contacts.import_contact.default_fields.contact.${f.name}`,
        )
          ? `${t(`contacts.import_contact.default_fields.contact.${f.name}`)}`
          : f.label,
      };
    });

  const operations: FilterOperationType[] = condition.field_type
    ? FieldBasedFilterOperations[condition.name] ||
      FieldBasedFilterOperations[ALL_FIELDS_BY_NAME[condition.field_type]]
    : [];

  const field = fields?.find(
    (field: FormServFieldType) => field.name === condition.name,
  );

  return (
    <React.Fragment key={key}>
      <ConditionRowContainer id={condition.id || ''} key={key}>
        <span className="icon" onClick={() => onDelete(condition.id || '')}>
          <Icon width="12" height="12" glyph="minus" />
        </span>
        <Dropdown
          defaultSelectedItem={
            (fieldsAvailableForSegments || []).find(
              (availableField: FormServFieldType) =>
                availableField.name === condition.name,
            ) || ''
          }
          emptyItemsText={`${t('no_results_found')}`}
          width="150px"
          type="input"
          disabled={disabled}
          filterKey="label"
          placeholder={`${t('forms.placeholders.search_fields')}`}
          itemToString={(v: any) => v.label}
          itemComponent={(v: any) => v.label}
          isErrored={showError && errors?.name?.message}
          items={fieldsAvailableForSegments || []}
          onChange={(option: FormServFieldType) => {
            if (!option || !onChange) return;
            onChange({
              id: condition.id,
              name: option.name,
              evaluate_on: 'CONTACT',
              field_type: ALL_FIELDS[option.type],
              operator: '',
              value: '',
            });
          }}
        />
        {condition.name && operations && operations.length > 0 && (
          <Dropdown
            defaultSelectedItem={
              (operations || []).find(
                (operation) => operation.id === condition.operator,
              ) || ''
            }
            emptyItemsText={`${t('no_results_found')}`}
            width="150px"
            disabled={disabled}
            itemToString={(v: FilterOperationType) => `${t(v.value)}`}
            itemComponent={(v: FilterOperationType) => `${t(v.value)}`}
            isErrored={showError && errors?.operator?.message}
            items={operations}
            onChange={(option: FilterOperationType) => {
              if (!option || !onChange) return;
              onChange({
                ...condition,
                operator: option.id,
                field_type:
                  option?.overrideFieldType && option?.inputValueType
                    ? ALL_FIELDS[option?.inputValueType]
                    : condition.field_type,
                value: '',
              });
            }}
          />
        )}
        {condition &&
          condition.operator &&
          FilterOperations &&
          FilterOperations[condition.operator] &&
          field && (
            <FilterOperation
              value={condition}
              errors={get(errors, ['value'], {})}
              field={field}
              showError={showError}
              onChange={onChange}
              userInfo={userInfo}
            />
          )}
      </ConditionRowContainer>
    </React.Fragment>
  );
};

export default SegmentConditionRow;
